// === Server Message Handlers ===
// Dispatches WebSocket messages from the server to the correct handler.
// App-level rendering/UI functions are injected via setupMessageHandlers() to avoid circular imports.

import {
  gitState,
  sessionState as stateSessionState,
} from '../state';
import {
  normalizeSessionSnapshot, normalizeActiveRuntime, cacheSessionSnapshot,
  cloneMessages, updateCachedSession, reconcileSessionCacheWithSessions,
  getSessionMeta, getAgentModeStorageKey, getSessionCacheDisposition,
} from '../session/cache';
import { deepClone } from '../utils';
import { emitWsEvent } from './events';
import { getAuthStartedAt, send } from './connection';
import { showToast, showBrowserNotification } from '../ui/toast';
import { CUSTOM_MODEL_OPTION_VALUE, MODE_LABELS } from '../constants';
import { logCodexBubbleDebug, logCodexSteeringDebug, logSessionDebug, logSessionFlowDebug, logSessionLoadDebug } from '../ui/session-debug';
import { handleRuntimeActivityMessage } from '../ui/runtime-activity';
import { toRuntimeActivitySegment, upsertRuntimeActivitySegment } from '../ui/chat/runtime-activity-segments';
import { createSessionLoadContext, getActiveSessionLoadContext } from '../session/session-load-context';
import { getHistoryResponseIgnoreReason } from '../session/history-request';
import { undismissVirtualProject } from '../ui/sidebar';
import { logProjectRemoveDebug } from '../ui/project-remove-debug';

// ---------------------------------------------------------------------------
// DI interface
// ---------------------------------------------------------------------------

interface MessageHandlerDeps {
  // DOM refs
  loginOverlay: HTMLElement;
  appEl: HTMLElement;
  loginError: HTMLElement;
  rememberPw: HTMLInputElement | null;
  chatTitle: HTMLElement;
  modeSelect: HTMLSelectElement;
  msgInput: HTMLTextAreaElement;
  costDisplay: HTMLElement;
  sendBtn: HTMLElement;
  abortBtn: HTMLElement;

  // Session / chat
  openSession: (id: string, opts?: any) => void;
  resetChatView: (agent: string) => void;
  restoreInitialSession: (agent: string) => void;
  renderSessionList: () => void;
  updateSessionItemLoadedMessageCount?: (sessionId: string) => void;
  setCurrentSessionRunningState: (running: boolean) => void;
  applySessionSnapshot: (snapshot: any, opts?: any) => void;
  finalizeLoadedSession: (sessionId: string) => void;
  finishSessionSwitch: (sessionId: string) => void;
  isBlockingSessionLoad: (sessionId: string) => boolean;
  prependHistoryMessages: (messages: any[], opts?: any) => void;

  // Rendering
  renderWorkspaceInsights: () => void;
  renderGitPanel: () => void;
  updateGitPanelBadge: () => void;
  appendError: (msg: string, opts?: any) => void;
  appendSystemMessage: (msg: string | any) => void;
  clearSessionLoading: () => void;
  startGenerating: (source?: string) => void;
  startNextStreamingTurn: () => void;
  finishGenerating: (sessionId?: string, source?: string) => void;
  renderStreamingSegments: (segments: any[]) => void;
  updateStreamingAssistantMeta: (message: any) => void;
  appendToolCall: (id: string, name: string, input: any, done: boolean, kind?: string | null, meta?: any) => void;
  updateToolCall: (id: string, result: any) => void;
  flushRender: () => void;
  scheduleRender: () => void;
  markStreamingProcessTextSegments: () => void;
  renderPendingAttachments: () => void;
  autoResize: () => void;

  // Auth / password
  showForceChangePassword: () => void;
  restoreRememberedPasswordInput: () => void;
  saveRememberedPassword: (pw: string) => void;
  clearRememberedPassword: () => void;
  handlePasswordChanged: (msg: any) => void;
  handleForcedLogout: (msg: any) => void;

  // Model pickers / UI
  updateAgentScopedUI: () => void;
  showOptionPicker: (title: string, opts: any[], active: string, cb: (v: string) => void) => void;
  showClaudeModelPicker: (entries: any[], models: any[], current: string, currentFull: string, onSelect?: (value: string) => void) => void;
  getCodexModelOptions: () => any[];
  getOpenCodeModelOptions: () => any[];

  // Dynamic callbacks (may change over session lifetime)
  getNativeSessions: () => ((groups: any[]) => void) | null;
  getCodexSessions: () => ((sessions: any[]) => void) | null;
  getCwdSuggestions: () => ((paths: any[]) => void) | null;
  getDirectoryListing: () => ((msg: any) => void) | null;

  // Mutable state — app.ts is source of truth in Phase 2
  connectionState: any;
  sessionState: any;
  composeState: any;
  getPendingAuthPassword: () => string | null;
  setPendingAuthPassword: (v: string | null) => void;
  getSelectedAgent: () => string;
  setModelConfigCache: (v: any) => void;
  setCodexConfigCache: (v: any) => void;
  setProjects: (v: any[]) => void;
  getPendingProjectSaveCallback: () => ((projects: any[]) => void) | null;
  setPendingProjectSaveCallback: (v: ((projects: any[]) => void) | null) => void;
}

let _d: MessageHandlerDeps | null = null;
const preloadingSessionIds = new Set<string>();
let activePreloadBatch: {
  pending: Set<string>;
  completedCount: number;
  failedCount: number;
  historyPendingCount: number;
  historyTotal: number;
} | null = null;
const resumedRuntimeIds = new Map<string, string>();
const resumedSessionIds = new Set<string>();
const RELOADABLE_ASSET_NAMES = new Set(['app.next.js', 'tw.css', 'style.css', 'msg-meta-bar.css']);

function logSessionFlow(event: string, payload: Record<string, unknown>): void {
  logSessionFlowDebug(event, payload);
}

// 预加载响应按批次汇总，避免 20 个会话各自刷一条连接效率日志。
function recordPreloadResult(sessionId: string, result: { failed?: boolean; historyPending?: boolean; historyTotal?: number }): void {
  if (!activePreloadBatch?.pending.has(sessionId)) return;
  activePreloadBatch.pending.delete(sessionId);
  activePreloadBatch.completedCount += 1;
  if (result.failed) activePreloadBatch.failedCount += 1;
  if (result.historyPending) activePreloadBatch.historyPendingCount += 1;
  if (Number.isFinite(result.historyTotal)) activePreloadBatch.historyTotal += Number(result.historyTotal);
  if (activePreloadBatch.pending.size > 0) return;
  logSessionLoadDebug('ws:session-info:preload-batch-complete', {
    completedCount: activePreloadBatch.completedCount,
    failedCount: activePreloadBatch.failedCount,
    historyPendingCount: activePreloadBatch.historyPendingCount,
    historyTotal: activePreloadBatch.historyTotal,
  });
  activePreloadBatch = null;
}

function streamingSnapshotDisplayModel(snapshot: any): string {
  return String(
    snapshot?.activeRuntime?.displayModel
      || snapshot?.activeRuntime?.model
      || snapshot?.displayModel
      || snapshot?.model
      || '',
  ).trim();
}

function ensureCachedStreamingAssistantMessage(snapshot: any): any {
  if (!Array.isArray(snapshot.messages)) snapshot.messages = [];
  const displayModel = streamingSnapshotDisplayModel(snapshot);
  const explicitModel = String(snapshot.model || '').trim();
  const lastMessage = snapshot.messages[snapshot.messages.length - 1];
  if (lastMessage?.role === 'assistant') {
    if (!Array.isArray(lastMessage.segments)) lastMessage.segments = [];
    if (!lastMessage.model && explicitModel) {
      lastMessage.model = explicitModel;
    }
    if (!lastMessage.displayModel && displayModel) {
      lastMessage.displayModel = displayModel;
    }
    return lastMessage;
  }
  const message = {
    role: 'assistant',
    content: '',
    segments: [],
    toolCalls: [],
    model: explicitModel,
    displayModel,
    timestamp: new Date().toISOString(),
    firstResponseAt: '',
    lastAppendedAt: '',
  };
  snapshot.messages.push(message);
  return message;
}

// 将缓存中的流式 assistant 切换到下一条独立 Codex turn。
function startNextCachedStreamingTurn(sessionId: string): void {
  updateCachedSession(sessionId, (snapshot) => {
    const previous = ensureCachedStreamingAssistantMessage(snapshot);
    const nextMessage = {
      role: 'assistant',
      content: '',
      segments: [],
      toolCalls: [],
      model: previous.model || '',
      displayModel: previous.displayModel || '',
      timestamp: new Date().toISOString(),
      firstResponseAt: '',
      lastAppendedAt: '',
    };
    snapshot.messages.push(nextMessage);
    snapshot.isRunning = true;
    snapshot.complete = false;
    logCodexBubbleDebug('cache-boundary', {
      sessionId,
      messageCount: snapshot.messages.length,
      assistantCount: snapshot.messages.filter((item: any) => item?.role === 'assistant').length,
      previousMessageIndex: snapshot.messages.indexOf(previous),
      nextMessageIndex: snapshot.messages.indexOf(nextMessage),
    });
  });
}

function touchAssistantMessageTimes(message: any, nowIso = new Date().toISOString()): void {
  if (!message.firstResponseAt) {
    message.firstResponseAt = message.timestamp || nowIso;
  }
  message.lastAppendedAt = nowIso;
}

function messageSignature(message: any): string {
  if (!message || typeof message !== 'object') return '';
  return JSON.stringify({
    role: message.role || '',
    timestamp: message.timestamp || '',
    content: message.content ?? '',
    attachments: Array.isArray(message.attachments) ? message.attachments : [],
    segments: Array.isArray(message.segments) ? message.segments : [],
    toolCalls: Array.isArray(message.toolCalls) ? message.toolCalls : [],
  });
}

function prependMessagesWithoutDuplication(existingMessages: any[], incomingMessages: any[]): any[] {
  const existing = Array.isArray(existingMessages) ? existingMessages : [];
  const incoming = Array.isArray(incomingMessages) ? incomingMessages : [];
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map((message) => messageSignature(message)).filter(Boolean));
  const dedupedIncoming = incoming.filter((message) => {
    const signature = messageSignature(message);
    if (!signature || seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
  return dedupedIncoming.concat(existing);
}

function syncCachedStreamingState(sessionId: string | null | undefined, updater: (snapshot: any, message: any) => void): void {
  const safeSessionId = typeof sessionId === 'string' ? sessionId : '';
  if (!safeSessionId) return;
  updateCachedSession(safeSessionId, (snapshot) => {
    const message = ensureCachedStreamingAssistantMessage(snapshot);
    snapshot.isRunning = true;
    snapshot.complete = false;
    updater(snapshot, message);
    if (!Array.isArray(message.segments)) message.segments = [];
    const textContent = message.segments
      .filter((segment: any) => segment?.type === 'text' && segment.phase !== 'process' && typeof segment.text === 'string')
      .map((segment: any) => segment.text)
      .join('');
    message.content = textContent;
    message.toolCalls = message.segments
      .filter((segment: any) => segment?.type === 'tool_call')
      .map((segment: any) => ({
        id: segment.id,
        name: segment.name,
        input: segment.input,
        result: segment.result,
        kind: segment.kind || null,
        meta: segment.meta || null,
        done: segment.done !== false,
      }));
    if (_d!.sessionState.currentAgent === 'codex') {
      logCodexBubbleDebug('cache-update', {
        sessionId: safeSessionId,
        messageCount: snapshot.messages.length,
        assistantCount: snapshot.messages.filter((item: any) => item?.role === 'assistant').length,
        currentMessageIndex: snapshot.messages.indexOf(message),
        segmentCount: message.segments.length,
        toolCallCount: message.toolCalls.length,
        contentLength: String(message.content || '').length,
        messageIdentity: message.clientMessageId || message.timestamp || null,
        messageOrder: snapshot.messages.slice(-12).map((item: any) => ({
          role: item?.role || null,
          identity: item?.clientMessageId || item?.timestamp || null,
          contentLength: String(item?.content || '').length,
        })),
      });
    }
    logSessionDebug('reply-history:stream-cache-update', {
      sessionId: safeSessionId,
      messageTimestamp: message.timestamp || null,
      firstResponseAt: message.firstResponseAt || null,
      lastAppendedAt: message.lastAppendedAt || null,
      segmentCount: Array.isArray(message.segments) ? message.segments.length : 0,
      toolCallCount: Array.isArray(message.toolCalls) ? message.toolCalls.length : 0,
      contentLength: String(message.content || '').length,
      contentTail: String(message.content || '').slice(-160),
    });
  });
}

function shouldReplaceSnapshotMessages(sessionId: string, incomingSnapshot: any): boolean {
  const cached = stateSessionState.sessionCache.get(sessionId) as any;
  if (!cached?.snapshot || !incomingSnapshot) return true;
  const cachedUpdated = String(cached.snapshot.updated || cached.version || '').trim();
  const incomingUpdated = String(incomingSnapshot.updated || '').trim();
  if (!cachedUpdated || !incomingUpdated) return true;
  if (incomingUpdated !== cachedUpdated) return incomingUpdated >= cachedUpdated;
  const cachedCount = Array.isArray(cached.snapshot.messages) ? cached.snapshot.messages.length : 0;
  const incomingCount = Array.isArray(incomingSnapshot.messages) ? incomingSnapshot.messages.length : 0;
  return incomingCount >= cachedCount;
}

function summarizeSessionSnapshotDecision(sessionId: string, incomingSnapshot: any): Record<string, unknown> {
  const cached = stateSessionState.sessionCache.get(sessionId) as any;
  return {
    sessionId,
    cachedUpdated: cached?.snapshot?.updated || cached?.version || null,
    incomingUpdated: incomingSnapshot?.updated || null,
    cachedCount: Array.isArray(cached?.snapshot?.messages) ? cached.snapshot.messages.length : null,
    incomingCount: Array.isArray(incomingSnapshot?.messages) ? incomingSnapshot.messages.length : null,
    cachedIsRunning: cached?.snapshot?.isRunning ?? null,
    incomingIsRunning: incomingSnapshot?.isRunning ?? null,
  };
}

function mergeSnapshotMetadataPreservingMessages(incomingSnapshot: any): any {
  const sessionId = String(incomingSnapshot?.sessionId || '').trim();
  const cached = sessionId ? (stateSessionState.sessionCache.get(sessionId) as any)?.snapshot : null;
  if (!cached?.messages) return incomingSnapshot;
  return {
    ...incomingSnapshot,
    messages: deepClone(cached.messages),
    historyBuffered: Number.isFinite(cached.historyBuffered)
      ? cached.historyBuffered
      : (Array.isArray(cached.messages) ? cached.messages.length : incomingSnapshot.historyBuffered),
    historyPending: cached.historyPending ?? incomingSnapshot.historyPending,
    historyRemaining: Number.isFinite(cached.historyRemaining)
      ? cached.historyRemaining
      : incomingSnapshot.historyRemaining,
    historyTotal: Number.isFinite(incomingSnapshot.historyTotal)
      ? Math.max(incomingSnapshot.historyTotal, Array.isArray(cached.messages) ? cached.messages.length : 0)
      : incomingSnapshot.historyTotal,
    complete: cached.complete ?? incomingSnapshot.complete,
  };
}

export function setupMessageHandlers(deps: MessageHandlerDeps): void {
  _d = deps;
}

function isActiveSessionScopedMessage(msg: any): boolean {
  const scopedSessionId = typeof msg?.sessionId === 'string' ? msg.sessionId.trim() : '';
  if (!scopedSessionId) return true;
  const currentSessionId = String(_d!.sessionState.currentSessionId || '').trim();
  return !!currentSessionId && scopedSessionId === currentSessionId;
}

// ---------------------------------------------------------------------------
// Individual handlers
// ---------------------------------------------------------------------------

function handleAuthResultMessage(msg: any): void {
  const pendingAuthPassword = _d!.getPendingAuthPassword();
  if (msg.success) {
    const authStarted = getAuthStartedAt();
    logSessionLoadDebug('client:auth-result', {
      success: true,
      durationMs: authStarted > 0 ? Date.now() - authStarted : null,
    });
    if (pendingAuthPassword !== null) {
      if (_d!.rememberPw?.checked) _d!.saveRememberedPassword(pendingAuthPassword);
      else _d!.clearRememberedPassword();
      _d!.setPendingAuthPassword(null);
    }
    _d!.connectionState.authToken = msg.token;
    localStorage.setItem('webcoding-token', msg.token);
    document.dispatchEvent(new CustomEvent('webcoding-auth-restored'));
    _d!.loginOverlay.hidden = true;
    _d!.appEl.hidden = false;
    send({ type: 'get_codex_config' });
    send({ type: 'get_projects' });
    send({ type: 'get_quick_messages' });
    if (msg.mustChangePassword) {
      _d!.showForceChangePassword();
    } else {
      _d!.connectionState.pendingInitialSessionLoad = true;
    }
    return;
  }
  logSessionLoadDebug('client:auth-result', { success: false });
  const attemptedPassword = pendingAuthPassword;
  _d!.setPendingAuthPassword(null);
  _d!.connectionState.authToken = null;
  localStorage.removeItem('webcoding-token');
  document.dispatchEvent(new CustomEvent('webcoding-auth-failed'));
  _d!.loginOverlay.hidden = false;
  _d!.appEl.hidden = true;
  if (attemptedPassword === null) _d!.restoreRememberedPasswordInput();
  _d!.loginError.textContent = msg.error || '密码错误';
  _d!.loginError.hidden = false;
}

function handleSessionDebugMessage(msg: any): void {
  if (!['connection-efficiency', 'codex-steering'].includes(msg?.group) || typeof msg?.event !== 'string') return;
  if (msg.group === 'codex-steering') {
    logCodexSteeringDebug(msg.event, {
      ...(msg.payload && typeof msg.payload === 'object' ? msg.payload : {}),
      source: 'server',
    });
    return;
  }
  logSessionLoadDebug(msg.event, {
    ...(msg.payload && typeof msg.payload === 'object' ? msg.payload : {}),
    source: 'server',
  });
}

function handleSessionListMessage(msg: any): void {
  const incomingSessions = Array.isArray(msg.sessions) ? msg.sessions : [];
  const currentSessionId = String(_d!.sessionState.currentSessionId || '').trim();
  const shouldPreserveCurrentRunning = !!(
    currentSessionId &&
    (_d!.composeState.isGenerating || _d!.sessionState.currentSessionRunning)
  );
  const nextSessions = shouldPreserveCurrentRunning
    ? incomingSessions.map((session: any) => {
      if (String(session?.id || '').trim() !== currentSessionId) return session;
      if (session?.isRunning) return session;
      return { ...session, isRunning: true };
    })
    : incomingSessions;
  logSessionFlow('ws:session-list', {
    count: nextSessions.length,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    pendingInitialSessionLoad: !!_d!.connectionState.pendingInitialSessionLoad,
    activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
    currentSessionRunning: !!_d!.sessionState.currentSessionRunning,
    isGenerating: !!_d!.composeState.isGenerating,
  });
  _d!.sessionState.sessions = nextSessions;
  stateSessionState.sessions = _d!.sessionState.sessions;
  reconcileSessionCacheWithSessions();
  _d!.renderSessionList();
  let preloadRequestCount = 0;
  let weakPreloadCacheCount = 0;
  let strongPreloadCacheCount = 0;
  const preloadSessionIds: string[] = [];
  for (const session of (_d!.sessionState.sessions || [])) {
    const sessionId = String(session?.id || '').trim();
    if (!sessionId || sessionId === _d!.sessionState.currentSessionId) continue;
    if (preloadingSessionIds.has(sessionId)) continue;
    preloadingSessionIds.add(sessionId);
    const cacheDisposition = getSessionCacheDisposition(sessionId);
    preloadRequestCount += 1;
    if (cacheDisposition === 'weak') weakPreloadCacheCount += 1;
    if (cacheDisposition === 'strong') strongPreloadCacheCount += 1;
    preloadSessionIds.push(sessionId);
  }
  if (preloadRequestCount > 0) {
    if (!activePreloadBatch) {
      activePreloadBatch = {
        pending: new Set(),
        completedCount: 0,
        failedCount: 0,
        historyPendingCount: 0,
        historyTotal: 0,
      };
    }
    for (const sessionId of preloadSessionIds) activePreloadBatch.pending.add(sessionId);
    logSessionFlow('ws:session-list:preload-batch-request', {
      currentSessionId: _d!.sessionState.currentSessionId || null,
      requestedCount: preloadRequestCount,
      weakCacheCount: weakPreloadCacheCount,
      strongCacheCount: strongPreloadCacheCount,
    });
    for (const sessionId of preloadSessionIds) send({ type: 'preload_session', sessionId });
  }
  if (_d!.sessionState.currentSessionId) {
    const currentMetaRunning = !!getSessionMeta(_d!.sessionState.currentSessionId)?.isRunning;
    const shouldPreserveRunningFromSessionList = !currentMetaRunning
      && (_d!.composeState.isGenerating || _d!.sessionState.currentSessionRunning);
    if (!shouldPreserveRunningFromSessionList) {
      _d!.setCurrentSessionRunningState(currentMetaRunning);
    }
  }
  if (_d!.connectionState.pendingInitialSessionLoad) {
    logSessionFlow('ws:session-list:resolve-initial-load', {
      currentSessionId: _d!.sessionState.currentSessionId || null,
      selectedAgent: _d!.getSelectedAgent(),
    });
    _d!.connectionState.pendingInitialSessionLoad = false;
    if (_d!.sessionState.currentSessionId) {
      _d!.openSession(_d!.sessionState.currentSessionId, {
        forceSync: true,
        blocking: false,
        context: createSessionLoadContext('reconnect_resync'),
      });
    } else {
      _d!.restoreInitialSession(_d!.getSelectedAgent());
    }
  } else if (_d!.sessionState.currentSessionId && !getSessionMeta(_d!.sessionState.currentSessionId)) {
    // Avoid transient session-list jitter during active generation from force-resetting the chat view.
    if (_d!.composeState.isGenerating || _d!.sessionState.currentSessionRunning) {
      logSessionFlow('ws:session-list:skip-reset-missing-current', {
        currentSessionId: _d!.sessionState.currentSessionId,
        isGenerating: !!_d!.composeState.isGenerating,
        currentSessionRunning: !!_d!.sessionState.currentSessionRunning,
      });
      return;
    }
    logSessionFlow('ws:session-list:reset-missing-current', {
      currentSessionId: _d!.sessionState.currentSessionId,
      selectedAgent: _d!.getSelectedAgent(),
    });
    _d!.resetChatView(_d!.getSelectedAgent());
  }
}

function handleSessionInfoMessage(msg: any): void {
  const rawMessages = Array.isArray(msg?.messages) ? msg.messages : [];
  logCodexSteeringDebug('client:session-info-before-normalize', {
    sessionId: msg?.sessionId || null,
    updated: msg?.updated || null,
    isRunning: !!msg?.isRunning,
    messageCount: rawMessages.length,
    userMessageCount: rawMessages.filter((message: any) => message?.role === 'user').length,
    messageIdentities: rawMessages.slice(-6).map((message: any) => ({
      role: message?.role || null,
      identity: message?.clientMessageId || message?.timestamp || null,
      contentLength: String(message?.content || '').length,
    })),
  });
  const snapshot = normalizeSessionSnapshot(msg);
  const shouldReplaceMessages = shouldReplaceSnapshotMessages(snapshot.sessionId, snapshot);
  const viewSnapshot = shouldReplaceMessages ? snapshot : mergeSnapshotMetadataPreservingMessages(snapshot);
  const messages = Array.isArray(viewSnapshot.messages) ? viewSnapshot.messages : [];
  logCodexSteeringDebug('client:session-info-message-window', {
    sessionId: snapshot.sessionId || null,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    messageCount: messages.length,
    userMessageCount: messages.filter((message: any) => message?.role === 'user').length,
    shouldReplaceMessages,
    isRunning: !!viewSnapshot.isRunning,
  });
  if (!msg.preloadOnly) {
    logSessionFlow('ws:session-info', {
      sessionId: msg.sessionId || null,
      preloadOnly: false,
      currentSessionId: _d!.sessionState.currentSessionId || null,
      activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
      activeLoadBlocking: !!_d!.sessionState.activeSessionLoad?.blocking,
      messageCount: messages.length,
      isRunning: !!viewSnapshot.isRunning,
      historyPending: !!viewSnapshot.historyPending,
      historyBuffered: Number.isFinite(viewSnapshot.historyBuffered) ? viewSnapshot.historyBuffered : null,
      historyTotal: Number.isFinite(viewSnapshot.historyTotal) ? viewSnapshot.historyTotal : null,
      shouldReplaceMessages,
    });
  }
  if (msg.preloadOnly) {
    preloadingSessionIds.delete(snapshot.sessionId);
    cacheSessionSnapshot(snapshot);
    recordPreloadResult(snapshot.sessionId, {
      historyPending: !!snapshot.historyPending,
      historyTotal: snapshot.historyTotal,
    });
    _d!.updateSessionItemLoadedMessageCount?.(snapshot.sessionId);
    return;
  }
  document.dispatchEvent(new CustomEvent('webcoding-session-snapshot', {
    detail: {
      sessionId: snapshot.sessionId,
      snapshot,
      messages: snapshot.messages || [],
    },
  }));

  const expectedSessionId = _d!.sessionState.activeSessionLoad?.sessionId;
  const isExpectedResponse = !expectedSessionId || expectedSessionId === msg.sessionId;
  const isCurrentSessionResponse = !!(
    _d!.sessionState.currentSessionId &&
    _d!.sessionState.currentSessionId === msg.sessionId
  );
  const shouldApplyToView = !!(
    (_d!.sessionState.activeSessionLoad?.sessionId && _d!.sessionState.activeSessionLoad.sessionId === msg.sessionId) ||
    isCurrentSessionResponse
  );

  if (!isExpectedResponse && _d!.sessionState.activeSessionLoad?.blocking) {
    logSessionFlow('ws:session-info:ignored-stale-blocking', {
      sessionId: msg.sessionId || null,
      expectedSessionId: expectedSessionId || null,
      currentSessionId: _d!.sessionState.currentSessionId || null,
    });
    logSessionLoadDebug('ws:session-info:ignored-stale-blocking', {
      sessionId: msg.sessionId || null,
      expectedSessionId: expectedSessionId || null,
      currentSessionId: _d!.sessionState.currentSessionId || null,
      activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
    });
    return;
  }

  if (!shouldApplyToView) {
    const wasPendingPreload = preloadingSessionIds.has(snapshot.sessionId);
    if (wasPendingPreload) preloadingSessionIds.delete(snapshot.sessionId);
    cacheSessionSnapshot(snapshot);
    logSessionLoadDebug('ws:session-info:cached-without-apply:cache-state', {
      sessionId: snapshot.sessionId || null,
      cacheDisposition: getSessionCacheDisposition(snapshot.sessionId),
      currentSessionId: _d!.sessionState.currentSessionId || null,
      activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
      wasPendingPreload,
      historyPending: !!msg.historyPending,
      historyBuffered: Number.isFinite(snapshot.historyBuffered) ? snapshot.historyBuffered : null,
      historyTotal: Number.isFinite(snapshot.historyTotal) ? snapshot.historyTotal : null,
    });
    logSessionLoadDebug('ws:session-info:cached-without-apply', {
      sessionId: msg.sessionId || null,
      currentSessionId: _d!.sessionState.currentSessionId || null,
      activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
      expectedSessionId: expectedSessionId || null,
      historyPending: !!msg.historyPending,
      wasPendingPreload,
      preloadOnly: !!msg.preloadOnly,
    });
    logSessionFlow('ws:session-info:cached-without-apply', {
      sessionId: msg.sessionId || null,
      currentSessionId: _d!.sessionState.currentSessionId || null,
      activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
      expectedSessionId: expectedSessionId || null,
      historyPending: !!msg.historyPending,
      wasPendingPreload,
      unexpectedPreloadPayload: wasPendingPreload,
      preloadOnly: !!msg.preloadOnly,
    });
    return;
  }
  if (_d!.sessionState.activeSessionLoad?.sessionId === msg.sessionId) {
    _d!.sessionState.activeSessionLoad.snapshot = snapshot;
  }
  const activeLoadContext = getActiveSessionLoadContext(_d!.sessionState.activeSessionLoad, msg.sessionId);
  const loadContext = activeLoadContext || (
    isCurrentSessionResponse
      ? createSessionLoadContext('active_session_refresh')
      : null
  );
  if (!activeLoadContext && loadContext) {
    logSessionLoadDebug('ws:session-info:fallback-active-session-context', {
      sessionId: msg.sessionId || null,
      reason: loadContext.reason,
    });
  }
  logSessionLoadDebug('ws:session-info:apply', {
    sessionId: msg.sessionId || null,
    activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
    loadContextReason: loadContext?.reason || null,
    immediate: _d!.isBlockingSessionLoad(msg.sessionId),
    isRunning: !!msg.isRunning,
    ...summarizeSessionSnapshotDecision(snapshot.sessionId, snapshot),
  });
  if (!shouldReplaceMessages) {
    logSessionFlow('ws:session-info:preserve-cached-messages', summarizeSessionSnapshotDecision(snapshot.sessionId, snapshot));
  }
  const preserveStreaming = msg.sessionId === _d!.sessionState.currentSessionId
    && (_d!.composeState.isGenerating || _d!.sessionState.currentSessionRunning)
    && (msg.isRunning || _d!.sessionState.currentSessionRunning);
  _d!.applySessionSnapshot(viewSnapshot, {
    immediate: _d!.isBlockingSessionLoad(msg.sessionId),
    suppressUnreadToast: false,
    preserveStreaming,
    loadContext,
  });
  if (!preserveStreaming) cacheSessionSnapshot(viewSnapshot);
  logSessionLoadDebug('ws:session-info:cache-after-apply', {
    sessionId: viewSnapshot.sessionId || null,
    cacheDisposition: getSessionCacheDisposition(viewSnapshot.sessionId),
    complete: !!viewSnapshot.complete,
    historyPending: !!viewSnapshot.historyPending,
    historyBuffered: Number.isFinite(viewSnapshot.historyBuffered) ? viewSnapshot.historyBuffered : null,
    historyTotal: Number.isFinite(viewSnapshot.historyTotal) ? viewSnapshot.historyTotal : null,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
  });
  if (_d!.sessionState.activeSessionLoad?.sessionId === msg.sessionId) {
    if (viewSnapshot.historyPending) {
      logSessionLoadDebug('ws:session-info:await-history', {
        sessionId: msg.sessionId || null,
        historyTotal: Number.isFinite(viewSnapshot.historyTotal) ? viewSnapshot.historyTotal : null,
        historyBuffered: Number.isFinite(viewSnapshot.historyBuffered) ? viewSnapshot.historyBuffered : null,
      });
      return;
    }
    _d!.finalizeLoadedSession(msg.sessionId);
    if (loadContext?.reason === 'reconnect_resync') {
      logSessionLoadDebug('client:connection-recovery-complete', {
        sessionId: msg.sessionId || null,
        messageCount: messages.length,
        historyPending: false,
      });
    }
    return;
  }
  logSessionFlow('ws:session-info:finish-nonactive', {
    sessionId: msg.sessionId || null,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
  });
  _d!.finishSessionSwitch(msg.sessionId);
}

function handleSessionHistoryChunkMessage(msg: any): void {
  logSessionFlow('ws:session-history-chunk', {
    sessionId: msg.sessionId || null,
    requestSeq: Number.isFinite(msg.requestSeq) ? msg.requestSeq : null,
    remaining: Number.isFinite(msg.remaining) ? msg.remaining : null,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    loadedHistorySessionId: _d!.sessionState.loadedHistorySessionId || null,
    activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
  });
  const ignoreReason = getHistoryResponseIgnoreReason({
    sessionId: msg.sessionId,
    currentSessionId: _d!.sessionState.currentSessionId,
    loadedHistorySessionId: _d!.sessionState.loadedHistorySessionId,
    activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId,
    expectedRequestSeq: _d!.sessionState.currentHistoryRequestSeq,
    responseRequestSeq: Number.isFinite(msg.requestSeq) ? msg.requestSeq : null,
  });
  if (ignoreReason) {
    logSessionLoadDebug('history:response-ignored', {
      sessionId: msg.sessionId || null,
      requestSeq: Number.isFinite(msg.requestSeq) ? msg.requestSeq : null,
      expectedRequestSeq: _d!.sessionState.currentHistoryRequestSeq || null,
      reason: ignoreReason,
      currentSessionId: _d!.sessionState.currentSessionId || null,
      loadedHistorySessionId: _d!.sessionState.loadedHistorySessionId || null,
      activeLoadSessionId: _d!.sessionState.activeSessionLoad?.sessionId || null,
    });
    return;
  }
  document.dispatchEvent(new CustomEvent('webcoding-session-history-chunk', {
    detail: {
      sessionId: msg.sessionId,
      messages: cloneMessages(msg.messages || []),
      remaining: msg.remaining,
      requestSeq: msg.requestSeq,
    },
  }));

  const blocking = _d!.isBlockingSessionLoad(msg.sessionId);
  _d!.prependHistoryMessages(msg.messages || [], {
    preserveScroll: !blocking,
    skipScrollbar: blocking,
  });
  updateCachedSession(msg.sessionId, (snapshot) => {
    snapshot.messages = prependMessagesWithoutDuplication(snapshot.messages, cloneMessages(msg.messages || []));
    snapshot.complete = !(_d!.sessionState.currentHistoryPending || msg.historyRemaining > 0 || msg.remaining > 0);
    snapshot.historyBuffered = Array.isArray(snapshot.messages) ? snapshot.messages.length : 0;
    snapshot.historyPending = !!(msg.historyRemaining > 0 || msg.remaining > 0);
  });
  if (msg.sessionId === _d!.sessionState.currentSessionId) {
    const appendedCount = Array.isArray(msg.messages) ? msg.messages.length : 0;
    _d!.sessionState.currentHistoryBuffered = (_d!.sessionState.currentHistoryBuffered || 0) + appendedCount;
    _d!.sessionState.currentHistoryPending = !!(msg.historyRemaining > 0 || msg.remaining > 0);
    _d!.sessionState.currentHistoryLoading = false;
    document.dispatchEvent(new CustomEvent('webcoding-history-state-changed', {
      detail: {
        source: 'session_history_chunk',
        sessionId: msg.sessionId,
        historyTotal: _d!.sessionState.currentHistoryTotal,
        historyBuffered: _d!.sessionState.currentHistoryBuffered,
        historyPending: _d!.sessionState.currentHistoryPending,
        historyLoading: _d!.sessionState.currentHistoryLoading,
        historyRemaining: msg.historyRemaining ?? null,
        remainingChunks: msg.remaining ?? null,
        requestSeq: Number.isFinite(msg.requestSeq) ? msg.requestSeq : null,
      },
    }));
  }
  if (_d!.sessionState.activeSessionLoad?.sessionId === msg.sessionId && !msg.remaining && !msg.historyRemaining) {
    const loadContext = _d!.sessionState.activeSessionLoad.context;
    const activeLoad = _d!.sessionState.activeSessionLoad;
    const historyRequestStartedAt = activeLoad.historyRequestStartedAt || 0;
    logSessionLoadDebug('history:complete', {
      requestSeq: activeLoad.historyRequestSeq || null,
      sessionId: msg.sessionId || null,
      messageCount: _d!.sessionState.currentHistoryBuffered,
      durationMs: historyRequestStartedAt ? Date.now() - historyRequestStartedAt : null,
      historyPending: false,
    });
    _d!.finalizeLoadedSession(msg.sessionId);
    if (loadContext?.reason === 'reconnect_resync') {
      logSessionLoadDebug('client:connection-recovery-complete', {
        sessionId: msg.sessionId || null,
        messageCount: _d!.sessionState.currentHistoryBuffered,
        historyPending: false,
      });
    }
  }
}

function handleSessionRenamedMessage(msg: any): void {
  _d!.sessionState.sessions = _d!.sessionState.sessions.map(
    (session) => session.id === msg.sessionId ? { ...session, title: msg.title } : session,
  );
  stateSessionState.sessions = _d!.sessionState.sessions;
  updateCachedSession(msg.sessionId, (snapshot) => { snapshot.title = msg.title; });
  if (msg.sessionId === _d!.sessionState.currentSessionId) {
    _d!.chatTitle.textContent = msg.title;
  }
  _d!.renderSessionList();
}

function handleTextDeltaMessage(msg: any): void {
  if (!isActiveSessionScopedMessage(msg)) return;
  if (!_d!.composeState.isGenerating) _d!.startGenerating('text_delta');
  const phase = msg.phase === 'process' ? 'process' : msg.phase === 'final' ? 'final' : undefined;
  if (_d!.sessionState.currentAgent === 'codex') {
    logCodexBubbleDebug('text-delta-received', {
      sessionId: msg.sessionId || _d!.sessionState.currentSessionId || null,
      textLength: String(msg.text || '').length,
      phase: phase || 'default',
      assistantCount: document.querySelectorAll('#messages .msg.assistant').length,
      streamingMessageIndex: Array.from(document.querySelectorAll('#messages .msg')).indexOf(document.getElementById('streaming-msg')),
      pendingTextLength: _d!.composeState.pendingText.length,
      activeBubbleId: document.getElementById('streaming-msg')?.getAttribute('data-bubble-id') || null,
      domMessageOrder: Array.from(document.querySelectorAll('#messages .msg')).slice(-12).map((item) => ({
        role: item.classList.contains('assistant') ? 'assistant' : item.classList.contains('user') ? 'user' : 'system',
        identity: item.getAttribute('data-message-identity') || null,
        bubbleId: item.getAttribute('data-bubble-id') || null,
        isStreaming: item.id === 'streaming-msg',
      })),
    });
  }
  logSessionDebug('reply-history:stream-delta', {
    sessionId: msg.sessionId || _d!.sessionState.currentSessionId || null,
    phase: phase || 'default',
    deltaLength: String(msg.text || '').length,
    deltaPreview: String(msg.text || '').slice(0, 160),
    deltaTail: String(msg.text || '').slice(-160),
  });
  if (phase !== 'process') _d!.composeState.pendingText += msg.text;
  const nowIso = new Date().toISOString();
  let nextSegments: any[] | null = null;
  syncCachedStreamingState(msg.sessionId || _d!.sessionState.currentSessionId, (_snapshot, message) => {
    touchAssistantMessageTimes(message, nowIso);
    const segments = Array.isArray(message.segments) ? message.segments : [];
    const lastSegment = segments[segments.length - 1];
    if (lastSegment?.type === 'text' && (!phase || lastSegment.phase === phase)) {
      lastSegment.text = `${lastSegment.text || ''}${msg.text || ''}`;
    } else {
      segments.push({ type: 'text', text: msg.text || '', ...(phase ? { phase } : {}) });
    }
    message.segments = segments;
    nextSegments = deepClone(segments);
    _d!.updateStreamingAssistantMeta(message);
  });
  if (phase === 'process' && nextSegments) {
    _d!.renderStreamingSegments(nextSegments);
    return;
  }
  _d!.scheduleRender();
}

function handleCodexTurnBoundaryMessage(msg: any): void {
  if (!isActiveSessionScopedMessage(msg)) return;
  const sessionId = String(msg.sessionId || _d!.sessionState.currentSessionId || '').trim();
  logCodexBubbleDebug('boundary-dispatched', {
    sessionId: sessionId || null,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    isGenerating: !!_d!.composeState.isGenerating,
    assistantCount: document.querySelectorAll('#messages .msg.assistant').length,
  });
  if (sessionId) startNextCachedStreamingTurn(sessionId);
  _d!.startNextStreamingTurn();
}

function handleToolStartMessage(msg: any): void {
  if (!isActiveSessionScopedMessage(msg)) return;
  if (!_d!.composeState.isGenerating) _d!.startGenerating('tool_start');
  if (_d!.composeState.pendingText) _d!.flushRender();
  _d!.markStreamingProcessTextSegments();
  _d!.composeState.activeToolCalls.set(msg.toolUseId, { name: msg.name, input: msg.input, kind: msg.kind || null, meta: msg.meta || null, done: false });
  const nowIso = new Date().toISOString();
  syncCachedStreamingState(msg.sessionId || _d!.sessionState.currentSessionId, (_snapshot, message) => {
    touchAssistantMessageTimes(message, nowIso);
    const segments = Array.isArray(message.segments) ? message.segments : [];
    segments.push({
      type: 'tool_call',
      id: msg.toolUseId,
      name: msg.name,
      input: deepClone(msg.input),
      kind: msg.kind || null,
      meta: msg.meta ? deepClone(msg.meta) : null,
      done: false,
    });
    message.segments = segments;
    _d!.updateStreamingAssistantMeta(message);
  });
  _d!.appendToolCall(msg.toolUseId, msg.name, msg.input, false, msg.kind || null, msg.meta || null);
}

function handleToolEndMessage(msg: any): void {
  if (!isActiveSessionScopedMessage(msg)) return;
  if (_d!.composeState.activeToolCalls.has(msg.toolUseId)) {
    _d!.composeState.activeToolCalls.get(msg.toolUseId).done = true;
    if (msg.kind) _d!.composeState.activeToolCalls.get(msg.toolUseId).kind = msg.kind;
    if (msg.meta) _d!.composeState.activeToolCalls.get(msg.toolUseId).meta = msg.meta;
    _d!.composeState.activeToolCalls.get(msg.toolUseId).result = msg.result;
  }
  const nowIso = new Date().toISOString();
  syncCachedStreamingState(msg.sessionId || _d!.sessionState.currentSessionId, (_snapshot, message) => {
    touchAssistantMessageTimes(message, nowIso);
    const segments = Array.isArray(message.segments) ? message.segments : [];
    const toolSegment = segments.find((segment: any) => segment?.type === 'tool_call' && segment.id === msg.toolUseId);
    if (toolSegment) {
      toolSegment.done = true;
      if (msg.kind) toolSegment.kind = msg.kind;
      if (msg.meta) toolSegment.meta = deepClone(msg.meta);
      toolSegment.result = deepClone(msg.result);
    }
    message.segments = segments;
    _d!.updateStreamingAssistantMeta(message);
  });
  _d!.updateToolCall(msg.toolUseId, msg.result);
}

function handleRuntimeActivityStreamMessage(msg: any): void {
  if (!isActiveSessionScopedMessage(msg)) return;
  if (!_d!.composeState.isGenerating) _d!.startGenerating('runtime_activity');
  const nowIso = new Date().toISOString();
  const nextSegment = toRuntimeActivitySegment(msg, _d!.sessionState.currentAgent || '', nowIso);
  if (!nextSegment) return;
  let nextSegments: any[] | null = null;
  syncCachedStreamingState(msg.sessionId || _d!.sessionState.currentSessionId, (_snapshot, message) => {
    touchAssistantMessageTimes(message, nowIso);
    const segments = upsertRuntimeActivitySegment(Array.isArray(message.segments) ? message.segments : [], nextSegment);
    message.segments = segments;
    nextSegments = deepClone(segments);
    _d!.updateStreamingAssistantMeta(message);
  });
  if (nextSegments) _d!.renderStreamingSegments(nextSegments);
}

function handleCostMessage(msg: any): void {
  if (!isActiveSessionScopedMessage(msg)) return;
  _d!.costDisplay.textContent = `$${msg.costUsd.toFixed(4)}`;
  if (_d!.sessionState.currentSessionId) {
    updateCachedSession(_d!.sessionState.currentSessionId, (snapshot) => { snapshot.totalCost = msg.costUsd; });
  }
  _d!.renderWorkspaceInsights();
}

function handleUsageMessage(msg: any): void {
  if (!isActiveSessionScopedMessage(msg)) return;
  if (msg.totalUsage) {
    const cacheText = msg.totalUsage.cachedInputTokens ? ` · cache ${msg.totalUsage.cachedInputTokens}` : '';
    _d!.costDisplay.textContent = `in ${msg.totalUsage.inputTokens} · out ${msg.totalUsage.outputTokens}${cacheText}`;
    if (_d!.sessionState.currentSessionId) {
      updateCachedSession(_d!.sessionState.currentSessionId, (snapshot) => { snapshot.totalUsage = deepClone(msg.totalUsage); });
    }
  }
  _d!.renderWorkspaceInsights();
}

function handleModeChangedMessage(msg: any): void {
  if (!msg.mode || !MODE_LABELS[msg.mode]) return;
  _d!.sessionState.currentMode = msg.mode;
  _d!.modeSelect.value = _d!.sessionState.currentMode;
  localStorage.setItem(getAgentModeStorageKey(_d!.sessionState.currentAgent), _d!.sessionState.currentMode);
  _d!.updateAgentScopedUI();
  if (_d!.sessionState.currentSessionId) {
    updateCachedSession(_d!.sessionState.currentSessionId, (snapshot) => { snapshot.mode = msg.mode; });
  }
  _d!.renderWorkspaceInsights();
}

function handleSteerResultMessage(msg: any): void {
  logCodexSteeringDebug('client:steer-result', {
    sessionId: msg.sessionId || null,
    clientMessageId: msg.clientMessageId || null,
    accepted: !!msg.accepted,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    currentSessionRunning: !!_d!.sessionState.currentSessionRunning,
    isGenerating: !!_d!.composeState.isGenerating,
    message: msg.message || null,
  });
  if (msg.accepted) return;
  if (msg.message) _d!.appendError(msg.message);
  if (msg.clientMessageId && msg.text) {
    _d!.msgInput.value = msg.text;
    _d!.autoResize();
  }
}

function handleModelChangedMessage(msg: any): void {
  if (msg.model === undefined) return;
  const normalizedRuntime = normalizeActiveRuntime(msg.activeRuntime, _d!.sessionState.currentAgent, msg.model || '');
  _d!.sessionState.currentModel = msg.model || '';
  _d!.sessionState.currentActiveRuntime = normalizedRuntime;
  _d!.sessionState.currentRuntimeCount = Number.isFinite(msg.runtimeCount)
    ? msg.runtimeCount
    : (normalizedRuntime?.runtimeCount || 0);
  if (_d!.sessionState.currentSessionId) {
    updateCachedSession(_d!.sessionState.currentSessionId, (snapshot) => {
      snapshot.model = msg.model || '';
      snapshot.activeRuntime = normalizedRuntime ? deepClone(normalizedRuntime) : null;
      snapshot.activeChannelKey = msg.activeChannelKey || normalizedRuntime?.channelKey || null;
      snapshot.runtimeCount = Number.isFinite(msg.runtimeCount)
        ? msg.runtimeCount
        : (normalizedRuntime?.runtimeCount || 0);
    });
  }
  _d!.updateAgentScopedUI();
  _d!.renderWorkspaceInsights();
}

function handleModelListMessage(msg: any): void {
  const sessionId = _d!.sessionState.currentSessionId;
  function selectModel(agent: string, value: string): void {
    if (value === CUSTOM_MODEL_OPTION_VALUE) {
      const customValue = String(window.prompt('输入模型 ID', _d!.sessionState.currentModel || '') || '').trim();
      if (!customValue) return;
      send({ type: 'set_session_model', sessionId, agent, model: customValue });
      return;
    }
    send({ type: 'set_session_model', sessionId, agent, model: value });
  }
  if ((msg.agent || _d!.sessionState.currentAgent) === 'codex') {
    const options = Array.isArray(msg.entries) && msg.entries.length > 0 ? msg.entries : _d!.getCodexModelOptions();
    const activeValue = msg.currentFull || _d!.sessionState.currentModel || 'default';
    _d!.showOptionPicker('选择 Codex 模型', options, activeValue, (value) => selectModel('codex', value));
    return;
  }
  if ((msg.agent || _d!.sessionState.currentAgent) === 'opencode') {
    const options = Array.isArray(msg.entries) && msg.entries.length > 0 ? msg.entries : _d!.getOpenCodeModelOptions();
    const activeValue = msg.currentFull || _d!.sessionState.currentModel || 'default';
    _d!.showOptionPicker('选择 OpenCode 模型', options, activeValue, (value) => selectModel('opencode', value));
    return;
  }
  if (msg.models) {
    _d!.showClaudeModelPicker(msg.entries, msg.models, msg.current, msg.currentFull, (value) => {
      send({ type: 'set_session_model', sessionId, agent: 'claude', model: value });
    });
  }
}

function handleResumeGeneratingMessage(msg: any): void {
  if (!isActiveSessionScopedMessage(msg)) return;
  const sessionId = String(msg.sessionId || _d!.sessionState.currentSessionId || '').trim();
  const runtimeId = String(msg.runtimeId || '').trim();
  if (sessionId && resumedSessionIds.has(sessionId)) {
    logSessionFlow('ws:resume-generating-duplicate-ignored', {
      sessionId,
      runtimeId,
      previousRuntimeId: resumedRuntimeIds.get(sessionId) || null,
      isGenerating: !!_d!.composeState.isGenerating,
      hasStreamingMessage: !!document.getElementById('streaming-msg'),
    });
    return;
  }
  if (sessionId) resumedSessionIds.add(sessionId);
  if (sessionId && runtimeId) resumedRuntimeIds.set(sessionId, runtimeId);
  logSessionFlow('ws:resume-generating', {
    sessionId: sessionId || null,
    runtimeId: runtimeId || null,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    textLength: typeof msg.text === 'string' ? msg.text.length : 0,
    toolCallCount: Array.isArray(msg.toolCalls) ? msg.toolCalls.length : 0,
    segmentCount: Array.isArray(msg.segments) ? msg.segments.length : 0,
  });
  _d!.setCurrentSessionRunningState(true);
  syncCachedStreamingState(msg.sessionId || _d!.sessionState.currentSessionId, (snapshot, message) => {
    snapshot.isRunning = true;
    snapshot.complete = false;
    if (!message.firstResponseAt && (msg.firstResponseAt || msg.timestamp)) {
      message.firstResponseAt = msg.firstResponseAt || msg.timestamp;
    }
    if (msg.lastAppendedAt) {
      message.lastAppendedAt = msg.lastAppendedAt;
    } else if (!message.lastAppendedAt) {
      message.lastAppendedAt = message.firstResponseAt || message.timestamp || new Date().toISOString();
    }
    if (Array.isArray(msg.segments) && msg.segments.length > 0) {
      message.segments = deepClone(msg.segments);
    } else {
      const segments = [];
      if (msg.text) segments.push({ type: 'text', text: msg.text });
      if (Array.isArray(msg.toolCalls)) {
        for (const toolCall of msg.toolCalls) {
          segments.push({
            type: 'tool_call',
            id: toolCall.id,
            name: toolCall.name,
            input: deepClone(toolCall.input),
            result: deepClone(toolCall.result),
            kind: toolCall.kind || null,
            meta: toolCall.meta ? deepClone(toolCall.meta) : null,
            done: toolCall.done !== false,
          });
        }
      }
      message.segments = segments;
    }
    _d!.updateStreamingAssistantMeta(message);
  });
  if (!_d!.composeState.isGenerating || !document.getElementById('streaming-msg')) {
    logSessionDebug('stop-debug:resume-streaming-start', {
      sessionId: msg.sessionId || _d!.sessionState.currentSessionId || null,
      isGenerating: !!_d!.composeState.isGenerating,
      hasStreamingMessage: !!document.getElementById('streaming-msg'),
    });
    _d!.startGenerating('resume_streaming');
  } else {
    _d!.sendBtn.hidden = true;
    _d!.abortBtn.hidden = false;
    _d!.composeState.activeToolCalls.clear();
    logSessionDebug('stop-debug:resume-streaming-reuse', {
      sessionId: msg.sessionId || _d!.sessionState.currentSessionId || null,
      isGenerating: !!_d!.composeState.isGenerating,
      sendHidden: !!_d!.sendBtn.hidden,
      abortHidden: !!_d!.abortBtn.hidden,
    });
    const bubble = document.querySelector('#streaming-msg .msg-bubble');
    if (bubble) (bubble as HTMLElement).innerHTML = '';
  }
  const resumedSegments = Array.isArray(msg.segments) && msg.segments.length > 0 ? msg.segments : null;
  if (resumedSegments) {
    _d!.renderStreamingSegments(resumedSegments);
    for (const segment of resumedSegments) {
      if (segment?.type !== 'tool_call' || !segment.id) continue;
      _d!.composeState.activeToolCalls.set(segment.id, {
        name: segment.name,
        input: segment.input,
        result: segment.result,
        kind: segment.kind || null,
        meta: segment.meta || null,
        done: segment.done !== false,
      });
    }
    _d!.composeState.pendingText = '';
    return;
  }
  _d!.composeState.pendingText = msg.text || '';
  _d!.flushRender();
  if (!(msg.toolCalls && msg.toolCalls.length > 0)) return;
  for (const tc of msg.toolCalls) {
    _d!.composeState.activeToolCalls.set(tc.id, {
      name: tc.name,
      input: tc.input,
      result: tc.result,
      kind: tc.kind || null,
      meta: tc.meta || null,
      done: tc.done,
    });
    _d!.appendToolCall(tc.id, tc.name, tc.input, tc.done, tc.kind || null, tc.meta || null);
    if (tc.done && tc.result) {
      _d!.updateToolCall(tc.id, tc.result);
    }
  }
}

function handleErrorMessage(msg: any): void {
  logSessionDebug('stop-debug:error', {
    sessionId: msg?.sessionId || null,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    isGenerating: !!_d!.composeState.isGenerating,
    critical: !!msg?.critical,
    fatal: !!msg?.fatal,
    message: msg?.message || '发生未知错误',
  });
  if (typeof msg?.sessionId === 'string' && msg.sessionId) {
    preloadingSessionIds.delete(msg.sessionId);
    recordPreloadResult(msg.sessionId, { failed: true });
  }
  if (!isActiveSessionScopedMessage(msg)) return;
  const errorMsg = msg.message || '发生未知错误';
  _d!.appendError(errorMsg);
  if (msg.critical || msg.fatal) {
    showToast(`错误: ${errorMsg}`, null);
  }
  _d!.clearSessionLoading();
  if (!_d!.composeState.isGenerating && _d!.sessionState.currentSessionId) {
    _d!.setCurrentSessionRunningState(!!getSessionMeta(_d!.sessionState.currentSessionId)?.isRunning);
  }
  if (_d!.composeState.isGenerating) _d!.finishGenerating(undefined, 'error');
}

function handleBackgroundDoneMessage(msg: any): void {
  logSessionFlow('ws:background-done', {
    sessionId: msg.sessionId || null,
    currentSessionId: _d!.sessionState.currentSessionId || null,
    title: msg.title || null,
    status: msg.status || 'completed',
    error: msg.error || null,
  });
  const failed = msg.status === 'failed';
  showToast(failed ? '后台任务失败' : '后台任务已完成', msg.sessionId);
  showBrowserNotification(msg.title, { status: msg.status || 'completed', error: msg.error || null });
  if (msg.sessionId === _d!.sessionState.currentSessionId) {
    logSessionLoadDebug('background-done:stop-active-session', {
      sessionId: msg.sessionId || null,
      wasGenerating: !!_d!.composeState.isGenerating,
      wasSessionRunning: !!_d!.sessionState.currentSessionRunning,
    });
    if (_d!.composeState.isGenerating || _d!.sessionState.currentSessionRunning) {
      _d!.finishGenerating(msg.sessionId, 'background_done');
    } else {
      _d!.setCurrentSessionRunningState(false);
    }
    updateCachedSession(msg.sessionId, (snapshot) => {
      snapshot.isRunning = false;
    });
    _d!.openSession(msg.sessionId, {
      forceSync: true,
      blocking: false,
      context: createSessionLoadContext('background_task_sync'),
    });
  } else {
    send({ type: 'list_sessions' });
  }
}

function handleModelConfigMessage(msg: any): void {
  _d!.setModelConfigCache(msg.config || null);
  emitWsEvent('model_config', msg.config);
}

function handleCodexConfigMessage(msg: any): void {
  _d!.setCodexConfigCache(msg.config || null);
  emitWsEvent('codex_config', msg.config);
}

function handleProjectsConfigMessage(msg: any): void {
  const updated = msg.projects || [];
  logProjectRemoveDebug('projects_config_received', {
    count: Array.isArray(updated) ? updated.length : null,
    projects: Array.isArray(updated)
      ? updated.map((project: any) => ({ id: project?.id || null, name: project?.name || null, path: project?.path || null }))
      : null,
  });
  // Clear dismiss for any path that now exists as a real saved project
  for (const project of updated) {
    if (project?.path) undismissVirtualProject(project.path);
  }
  _d!.setProjects(updated);
  _d!.renderSessionList();
  const cb = _d!.getPendingProjectSaveCallback();
  if (cb) {
    _d!.setPendingProjectSaveCallback(null);
    cb(updated);
  }
}

function handleGitResultMessage(msg: any): void {
  const responseCwd = msg?.data?.cwd || msg?.cwd || null;
  const staleForCurrentView = !!(responseCwd && _d!.sessionState.currentCwd && responseCwd !== _d!.sessionState.currentCwd);

  if (!staleForCurrentView) {
    gitState.loading = false;
    if (msg.success) {
      gitState.lastError = '';
      if (msg.action === 'status') {
        const nextRepoRoot = msg.data?.repoRoot || null;
        const prevRepoRoot = gitState.status?.repoRoot || gitState.cwd || null;
        gitState.cwd = responseCwd || _d!.sessionState.currentCwd || null;
        gitState.status = msg.data || null;
        gitState.filesExpanded = false;
        if (nextRepoRoot !== prevRepoRoot) {
          gitState.collapsedTreeNodes = [];
        }
      } else if (msg.action === 'log') {
        gitState.logEntries = Array.isArray(msg.data?.entries) ? msg.data.entries : [];
      } else if (msg.action === 'branch' && Array.isArray(msg.data?.branches)) {
        gitState.branchEntries = msg.data.branches;
      }
    } else {
      gitState.lastError = msg.error || 'Git 操作失败';
      if (msg.action === 'status') gitState.status = null;
    }
    _d!.renderWorkspaceInsights();
    if (msg.action === 'status') {
      _d!.updateGitPanelBadge();
      if (gitState.panelOpen) _d!.renderGitPanel();
    }
  }

  emitWsEvent('git_result', msg);
}

function handleAssetVersionChangedMessage(msg: any): void {
  const win = window as any;
  if (!RELOADABLE_ASSET_NAMES.has(msg?.name)) return;
  if (!msg?.version) return;
  if (!win.__wcAssetVersions) win.__wcAssetVersions = {};
  const currentVersion = win.__wcAssetVersions[msg.name] || null;
  if (currentVersion && msg.version === currentVersion) return;
  console.info('[wc-next] asset version changed, reloading', {
    name: msg.name,
    from: currentVersion,
    to: msg.version,
  });
  win.__wcAssetVersions[msg.name] = msg.version;
  if (msg.name === 'app.next.js') win.__wcAssetVersion = msg.version;
  location.reload();
}

function handleSessionTruncatedForEditMessage(msg: any): void {
  if (!_d) return;
  if (msg?.sessionId && _d.sessionState.currentSessionId && msg.sessionId !== _d.sessionState.currentSessionId) return;
  _d.msgInput.value = typeof msg?.text === 'string' ? msg.text : '';
  _d.composeState.pendingAttachments = [];
  _d.renderPendingAttachments();
  _d.autoResize();
  _d.msgInput.focus();
  showToast('会话已截断，可编辑后重新发送');
}

// ---------------------------------------------------------------------------
// Dispatch table + main entry point
// ---------------------------------------------------------------------------

const SERVER_MESSAGE_HANDLERS: Record<string, (msg: any) => void> = Object.freeze({
  auth_result: handleAuthResultMessage,
  session_debug: handleSessionDebugMessage,
  session_list: handleSessionListMessage,
  session_info: handleSessionInfoMessage,
  session_truncated_for_edit: handleSessionTruncatedForEditMessage,
  session_history_chunk: handleSessionHistoryChunkMessage,
  session_renamed: handleSessionRenamedMessage,
  text_delta: handleTextDeltaMessage,
  codex_turn_boundary: handleCodexTurnBoundaryMessage,
  tool_start: handleToolStartMessage,
  tool_end: handleToolEndMessage,
  runtime_activity: (msg) => {
    if (!isActiveSessionScopedMessage(msg)) return;
    handleRuntimeActivityStreamMessage(msg);
    handleRuntimeActivityMessage(msg);
  },
  cost: handleCostMessage,
  usage: handleUsageMessage,
  done: (msg) => {
    syncCachedStreamingState(msg.sessionId || _d!.sessionState.currentSessionId, (snapshot) => {
      snapshot.isRunning = false;
    });
    logSessionDebug('stop-debug:done', {
      sessionId: msg.sessionId || null,
      currentSessionId: _d!.sessionState.currentSessionId || null,
      isGenerating: !!_d!.composeState.isGenerating,
      totalCost: msg.totalCost ?? null,
      totalUsage: msg.totalUsage || null,
      isActiveSessionMessage: isActiveSessionScopedMessage(msg),
    });
    logSessionFlow('ws:done', {
      sessionId: msg.sessionId || null,
      currentSessionId: _d!.sessionState.currentSessionId || null,
      totalCost: msg.totalCost ?? null,
      totalUsage: msg.totalUsage || null,
      isActiveSessionMessage: isActiveSessionScopedMessage(msg),
    });
    if (isActiveSessionScopedMessage(msg)) {
      resumedSessionIds.delete(String(msg.sessionId || '').trim());
      resumedRuntimeIds.delete(String(msg.sessionId || '').trim());
      _d!.finishGenerating(msg.sessionId, 'done');
      if (msg.sessionId) {
        logSessionFlow('ws:done:request-completion-sync', {
          sessionId: msg.sessionId,
          label: '正在同步最终回复…',
        });
        _d!.openSession(msg.sessionId, {
          forceSync: true,
          blocking: false,
          label: '正在同步最终回复…',
          context: createSessionLoadContext('completion_sync'),
        });
      }
    }
  },
  system_message: (msg) => {
    if (!isActiveSessionScopedMessage(msg)) return;
    _d!.appendSystemMessage({
      role: 'system',
      content: msg.message,
      retryAction: msg.retryAction || null,
      timestamp: msg.timestamp || null,
    });
  },
  mode_changed: handleModeChangedMessage,
  model_changed: handleModelChangedMessage,
  model_list: handleModelListMessage,
  resume_generating: handleResumeGeneratingMessage,
  error: handleErrorMessage,
  steer_result: handleSteerResultMessage,
  notify_config: (msg) => { emitWsEvent('notify_config', msg.config); },
  quick_messages_config: (msg) => { emitWsEvent('quick_messages_config', { groups: msg.groups || [], items: msg.items || [] }); },
  notify_test_result: (msg) => { emitWsEvent('notify_test_result', msg); },
  tunnel_status: (msg) => { emitWsEvent('tunnel_status', msg); },
  tunnel_install_progress: (msg) => { emitWsEvent('tunnel_install_progress', msg); },
  model_config: handleModelConfigMessage,
  codex_config: handleCodexConfigMessage,
  fetch_models_result: (msg) => { emitWsEvent('fetch_models_result', msg); },
  background_done: handleBackgroundDoneMessage,
  password_changed: (msg) => _d!.handlePasswordChanged(msg),
  force_logout: (msg) => _d!.handleForcedLogout(msg),
  native_sessions: (msg) => { const fn = _d!.getNativeSessions(); if (fn) fn(msg.groups || []); },
  codex_sessions: (msg) => { const fn = _d!.getCodexSessions(); if (fn) fn(msg.groups || msg.sessions || []); },
  cwd_suggestions: (msg) => { const fn = _d!.getCwdSuggestions(); if (fn) fn(msg.paths || []); },
  directory_listing: (msg) => { const fn = _d!.getDirectoryListing(); if (fn) fn(msg); },
  asset_version_changed: handleAssetVersionChangedMessage,
  update_info: (msg) => { if (typeof (window as any)._ccOnUpdateInfo === 'function') (window as any)._ccOnUpdateInfo(msg); },
  git_result: handleGitResultMessage,
  projects_config: handleProjectsConfigMessage,
});

export function handleServerMessage(msg: unknown): void {
  const m = msg as any;
  const handler = SERVER_MESSAGE_HANDLERS[m?.type];
  if (typeof handler === 'function') handler(m);
}
